import { net } from './net.js';
import { storage } from './storage.js';
import { normalizeText, uid } from './utils.js';
import { cleanSegments } from './segments.js';

/* Biblioteca propia en un servidor Plex. Mismo papel que el cliente de Jellyfin/Emby, con la API
   de Plex: se entra con la dirección del servidor y su token (X-Plex-Token), que se guarda en el
   campo de contraseña del proveedor. Plex contesta en JSON si se le pide con «Accept» y en XML si
   no (por ejemplo a través del proxy), así que se entienden los dos. */

const PAGE = 300;

function fail(code, status) {
    return Object.assign(new Error(code), { code, status });
}

const catId = (name) => 'g-' + normalizeText(name).replace(/\s+/g, '-');
const list = (x) => (Array.isArray(x) ? x : x ? [x] : []);

/** XML de Plex → la misma estructura que su JSON: atributos como propiedades e hijos como listas. */
function xmlNode(el) {
    const out = {};
    for (let i = 0; i < el.attributes.length; i++) out[el.attributes[i].name] = el.attributes[i].value;
    for (let i = 0; i < el.children.length; i++) {
        const child = el.children[i];
        (out[child.tagName] = out[child.tagName] || []).push(xmlNode(child));
    }
    return out;
}

/** Respuesta de Plex (JSON o XML) → contenido de MediaContainer. En XML los elementos (Video,
    Directory) se exponen también como `Metadata`, que es como los llama el JSON. */
export function parsePlex(text) {
    const s = String(text || '').trim();
    if (s.charAt(0) === '{') return (JSON.parse(s).MediaContainer) || {};
    if (typeof DOMParser === 'undefined') throw fail('bad_content');
    const doc = new DOMParser().parseFromString(s, 'application/xml');
    const root = doc.documentElement;
    if (!root || root.tagName !== 'MediaContainer') throw fail('bad_content');
    const mc = xmlNode(root);
    if (!mc.Metadata) mc.Metadata = (mc.Video || []).concat(mc.Directory || []);
    return mc;
}

export class PlexClient {
    constructor(profile, { fetchText = null } = {}) {
        this.profile = profile;
        this.host = String(profile.host || '').replace(/\/+$/, '');
        this.token = String(profile.password || profile.token || '');
        this.fetchText = fetchText || ((url, opts) => this._fetchText(url, opts));
    }

    async _fetchText(url, opts) {
        // Directo pidiendo JSON; si el navegador no deja (contenido mixto, CORS), por el proxy.
        try {
            const r = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
            if (r.status === 401) throw fail('auth', 401);
            if (!r.ok) throw fail('http', r.status);
            return await r.text();
        } catch (e) {
            if (e.code) throw e;
            return net.fetchText(url, Object.assign({ forceProxy: true }, opts));
        }
    }

    _url(path, params) {
        const u = new URL(this.host + path);
        Object.keys(params || {}).forEach((k) => { if (params[k] != null && params[k] !== '') u.searchParams.set(k, params[k]); });
        if (this.token) u.searchParams.set('X-Plex-Token', this.token);
        return u.toString();
    }

    async get(path, params, opts) {
        let text;
        try {
            text = await this.fetchText(this._url(path, params), opts);
        } catch (e) {
            if (e.status === 401) throw fail('auth', 401);
            throw e;
        }
        return parsePlex(text);
    }

    async auth() {
        const mc = await this.get('/', null, { timeout: 10000 });
        return {
            user_info: { username: mc.myPlexUsername || '', auth: 1, status: 'Active', exp_date: null, max_connections: 0, active_cons: 0 },
            server_info: { url: this.host.replace(/^https?:\/\//, ''), name: mc.friendlyName || '', version: mc.version || '', product: 'Plex' }
        };
    }

    image(path, width, height) {
        if (!path) return '';
        return this._url('/photo/:/transcode', { width, height, minSize: 1, upscale: 1, url: path });
    }

    /** Lo visto según el servidor: { position (s), played, at (ms) }, o null si no trae nada. */
    _serverState(m) {
        if (m.viewOffset == null && m.viewCount == null && m.lastViewedAt == null) return null;
        const offset = Number(m.viewOffset) || 0;
        return { position: Math.round(offset / 1000), played: (Number(m.viewCount) || 0) > 0 && !offset, at: (Number(m.lastViewedAt) || 0) * 1000 };
    }

    _base(m) {
        const genres = list(m.Genre).map((g) => g.tag).filter(Boolean);
        const media = list(m.Media)[0] || {};
        const part = list(media.Part)[0] || {};
        const tmdb = list(m.Guid).map((g) => /^tmdb:\/\/(\d+)/.exec(g.id || '')).filter(Boolean)[0];
        const imdb = list(m.Guid).map((g) => /^imdb:\/\/(tt\d+)/.exec(g.id || '')).filter(Boolean)[0];
        return {
            id: String(m.ratingKey),
            name: m.title || '',
            originalName: m.originalTitle || '',
            poster: this.image(m.thumb, 500, 750),
            backdrop: this.image(m.art, 1280, 720),
            categoryId: genres.length ? catId(genres[0]) : 'g-otros',
            categoryName: genres[0] || '',
            genre: genres.join(', '),
            plot: m.summary || '',
            year: m.year ? String(m.year) : '',
            rating: Math.round((Number(m.audienceRating) || Number(m.rating) || 0) * 10) / 10,
            added: Number(m.addedAt) || 0,
            age: m.contentRating || '',
            tmdb: tmdb ? tmdb[1] : '',
            imdb: imdb ? imdb[1] : '',
            durationSecs: m.duration ? Math.round(Number(m.duration) / 1000) : 0,
            ext: String(part.container || media.container || 'mp4'),
            // Dirección del fichero dentro del servidor; con ella se monta la del vídeo.
            part: part.key || '',
            video: media.videoCodec ? { codec_name: String(media.videoCodec).toLowerCase(), width: Number(media.width) || 0, height: Number(media.height) || 0 } : null,
            audio: media.audioCodec ? { codec_name: String(media.audioCodec).toLowerCase(), channels: Number(media.audioChannels) || 0 } : null,
            bitrate: Number(media.bitrate) || 0,
            server: this._serverState(m)
        };
    }

    async _section(key, type) {
        const out = [];
        for (let start = 0; ; start += PAGE) {
            const mc = await this.get('/library/sections/' + key + '/all', { type, includeGuids: 1, 'X-Plex-Container-Start': start, 'X-Plex-Container-Size': PAGE }, { timeout: 60000 });
            const items = list(mc.Metadata);
            out.push.apply(out, items);
            if (items.length < PAGE || out.length >= (Number(mc.totalSize) || 0)) break;
        }
        return out;
    }

    async fetchCatalog() {
        const account = await this.auth();
        const sections = list((await this.get('/library/sections', null, { timeout: 20000 })).Directory);
        const vod = [];
        const series = [];
        for (const s of sections) {
            if (s.type === 'movie') (await this._section(s.key, 1)).forEach((m) => vod.push(this._base(m)));
            else if (s.type === 'show') (await this._section(s.key, 2)).forEach((m) => series.push(Object.assign(this._base(m), { ext: '', part: '' })));
        }
        vod.sort((a, b) => b.added - a.added);
        series.sort((a, b) => b.added - a.added);
        const cats = (items) => {
            const seen = new Map();
            items.forEach((x) => { if (!seen.has(x.categoryId)) seen.set(x.categoryId, { id: x.categoryId, name: x.categoryName || 'Otros' }); });
            return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
        };
        return {
            updatedAt: Date.now(),
            account: { user: account.user_info, server: account.server_info },
            live: [],
            vod,
            series,
            categories: { live: [], vod: cats(vod), series: cats(series) }
        };
    }

    async info(kind, id, base) {
        const mc = await this.get('/library/metadata/' + id, { includeGuids: 1 }, { timeout: 20000 });
        const m = list(mc.Metadata)[0] || {};
        const roles = list(m.Role);
        const out = Object.assign({}, base, this._base(m), { id: base.id, categoryId: base.categoryId }, {
            cast: roles.map((r) => r.tag).join(', '),
            castList: roles.slice(0, 15).map((r) => ({ name: r.tag, character: r.role || '', photo: r.thumb || '' })),
            director: list(m.Director).map((d) => d.tag).join(', '),
            country: (list(m.Country)[0] || {}).tag || '',
            releaseDate: m.originallyAvailableAt || '',
            youtube: ''
        });
        if (kind !== 'series') return out;
        const leaves = list((await this.get('/library/metadata/' + id + '/allLeaves', null, { timeout: 60000 })).Metadata);
        const seasons = new Map();
        leaves.forEach((ep) => {
            const number = Number(ep.parentIndex);
            if (!(number > 0) || ep.index == null) return;
            if (!seasons.has(number)) seasons.set(number, { number, name: ep.parentTitle || ('Temporada ' + number), poster: '', overview: '', episodes: [] });
            const b = this._base(ep);
            seasons.get(number).episodes.push({
                id: b.id, seriesId: base.id, season: number, num: Number(ep.index), title: ep.title || ('Episodio ' + ep.index),
                ext: b.ext, part: b.part, plot: b.plot, durationSecs: b.durationSecs, duration: '', thumb: this.image(ep.thumb, 400, 225),
                rating: b.rating, airDate: ep.originallyAvailableAt || '', video: b.video, audio: b.audio, bitrate: b.bitrate, server: b.server
            });
        });
        out.seasons = Array.from(seasons.values()).sort((a, b) => a.number - b.number);
        out.seasons.forEach((s) => s.episodes.sort((a, b) => a.num - b.num));
        return out;
    }

    /** Colecciones de las bibliotecas de películas y series: [{ id, name, poster, ids: [ratingKey de cada título] }], las de 2 títulos o más. */
    async collections(limit = 30) {
        const sections = list((await this.get('/library/sections', null, { timeout: 20000 })).Directory).filter((s) => s.type === 'movie' || s.type === 'show');
        const out = [];
        for (const s of sections) {
            const boxes = list((await this.get('/library/sections/' + s.key + '/collections', null, { timeout: 30000 }).catch(() => ({}))).Metadata);
            for (const b of boxes) {
                if (out.length >= limit) return out;
                const kids = list((await this.get('/library/collections/' + b.ratingKey + '/children', null, { timeout: 30000 }).catch(() => ({}))).Metadata);
                const ids = kids.map((k) => String(k.ratingKey));
                if (ids.length >= 2) out.push({ id: String(b.ratingKey), name: b.title || '', poster: this.image(b.thumb, 500, 750), ids });
            }
        }
        return out;
    }

    /** Candidatos para «Corregir emparejado»: [{ key, title, year, thumb, overview, source, raw }]. */
    async matchSearch(kind, id, { title = '', year = '' } = {}) {
        let mc;
        try {
            mc = await this.get('/library/metadata/' + id + '/matches', { manual: 1, title, year }, { timeout: 30000 });
        } catch (e) {
            if (e.status === 401 || e.status === 403) throw fail('forbidden', e.status);
            throw e;
        }
        return list(mc.SearchResult).slice(0, 12).map((x, i) => ({ key: String(i), title: x.name || '', year: x.year ? String(x.year) : '', thumb: x.thumb || '', overview: x.summary || '', source: 'Plex', raw: { guid: x.guid, name: x.name, year: x.year } }));
    }

    async matchApply(kind, id, candidate) {
        const r = await fetch(this._url('/library/metadata/' + id + '/match', { guid: candidate.raw.guid, name: candidate.raw.name, year: candidate.raw.year }), { method: 'PUT', headers: { Accept: 'application/json' } });
        if (r.status === 401 || r.status === 403) throw fail('forbidden', r.status);
        if (!r.ok) throw fail('http', r.status);
    }

    /** Marcas de intro y de créditos (Plex las detecta con «Detectar intros», una función de Plex Pass): [{ type, start, end }] en segundos. */
    async segments(id, durationSecs = 0) {
        try {
            const mc = await this.get('/library/metadata/' + id, { includeMarkers: 1 }, { timeout: 8000 });
            const m = list(mc.Metadata)[0] || {};
            return cleanSegments(list(m.Marker).map((x) => ({
                type: x.type === 'intro' ? 'intro' : x.type === 'credits' ? 'credits' : '',
                start: Number(x.startTimeOffset) / 1000, end: Number(x.endTimeOffset) / 1000
            })), durationSecs || (m.duration ? Number(m.duration) / 1000 : 0));
        } catch (e) {
            return [];
        }
    }

    _clientId() {
        let id = storage.get('deviceId', '');
        if (!id) {
            id = uid() + uid();
            storage.set('deviceId', id);
        }
        return id;
    }

    /**
     * Cuenta al servidor lo que se está viendo («timeline») y, al terminar, lo marca como visto («scrobble»). Plex pide
     * identificar la aplicación en cada aviso. Las respuestas no interesan.
     */
    async report(id, { position = 0, duration = 0, stopped = false, finished = false } = {}) {
        const who = { 'X-Plex-Client-Identifier': this._clientId(), 'X-Plex-Product': 'Filoteca', 'X-Plex-Device': 'Filoteca', 'X-Plex-Platform': 'Web' };
        await this.fetchText(this._url('/:/timeline', Object.assign({
            ratingKey: id, key: '/library/metadata/' + id, identifier: 'com.plexapp.plugins.library',
            state: stopped ? 'stopped' : 'playing', time: Math.max(0, Math.round(position * 1000)), duration: Math.max(0, Math.round(duration * 1000))
        }, who)));
        if (finished) await this.fetchText(this._url('/:/scrobble', Object.assign({ key: id, identifier: 'com.plexapp.plugins.library' }, who)));
    }

    /** Dirección del fichero original. `part` la trae cada título; si falta, la descarga por id. */
    _stream(id, ext, part) {
        return this._url(part || ('/library/metadata/' + id + '/file' + (ext ? '.' + ext : '')), { download: 0 });
    }

    vodUrl(id, ext, item) {
        return this._stream(id, ext, item && item.part);
    }

    seriesUrl(id, ext, item) {
        return this._stream(id, ext, item && item.part);
    }

    liveUrl() {
        return '';
    }

    xmltvUrl() {
        return '';
    }
}
